/**
 * 转发消息数据结构定义
 *
 * 转发消息使用 MESSAGE_TYPE.TEXT (type: 0)，通过特殊的 JSON 内容结构来标识
 */

/**
 * 转发消息内容结构
 * @typedef {Object} ForwardMessageContent
 * @property {string} type - 固定值 "FORWARD"，用于标识转发消息
 * @property {string} version - 版本号，用于未来兼容性
 * @property {string} title - 显示标题，默认 "聊天记录"
 * @property {number} messageCount - 转发的消息数量
 * @property {ForwardedMessage[]} messages - 转发的消息列表
 * @property {string} summary - 自动生成的摘要，如 "张三、李四等人的聊天记录"
 * @property {number} createTime - 转发消息创建时间戳
 */

/**
 * 转发的单条消息结构
 * @typedef {Object} ForwardedMessage
 * @property {string|number} id - 原始消息ID
 * @property {number} senderId - 发送者用户ID
 * @property {string} senderName - 发送者显示名称
 * @property {string} senderAvatar - 发送者头像URL
 * @property {string} content - 消息文本内容
 * @property {number} type - 原始消息类型 (0:TEXT, 1:IMAGE, 2:FILE, 3:AUDIO)
 * @property {number} timestamp - 消息发送时间戳
 * @property {string} [mediaUrl] - 图片/文件URL（非文本消息）
 * @property {string} [fileName] - 文件名（FILE类型）
 * @property {number} [fileSize] - 文件大小（FILE类型）
 * @property {string} [thumbUrl] - 缩略图URL（IMAGE类型）
 */

/**
 * 创建转发消息内容结构
 * @param {Array} messages - 要转发的消息列表
 * @param {Object} chat - 当前会话信息
 * @param {Object} [options] - 可选配置
 * @returns {ForwardMessageContent}
 */
export function createForwardMessageContent(messages, chat, options = {}) {
    // 按时间戳排序
    const sortedMessages = [...messages].sort((a, b) => a.sendTime - b.sendTime);

    // 提取消息数据
    const messageData = sortedMessages.map(msg => {
        const data = {
            id: msg.id || msg.tmpId,
            senderId: msg.sendId,
            senderName: msg.sendNickName || msg.senderName || '未知',
            senderAvatar: msg.senderAvatar || '',
            content: msg.content || '',
            type: msg.type,
            timestamp: msg.sendTime
        };

        // 处理非文本消息的额外信息
        try {
            if (msg.type === 1) { // IMAGE
                const imageData = typeof msg.content === 'string' ? JSON.parse(msg.content) : msg.content;
                data.mediaUrl = imageData.originUrl || imageData.url;
                data.thumbUrl = imageData.thumbUrl;
            } else if (msg.type === 2) { // FILE
                const fileData = typeof msg.content === 'string' ? JSON.parse(msg.content) : msg.content;
                data.mediaUrl = fileData.url;
                data.fileName = fileData.name;
                data.fileSize = fileData.size;
            } else if (msg.type === 3) { // AUDIO
                const audioData = typeof msg.content === 'string' ? JSON.parse(msg.content) : msg.content;
                data.mediaUrl = audioData.url;
            }
        } catch (e) {
            console.warn('[ForwardMessage] 解析消息内容失败:', e);
        }

        return data;
    });

    // 生成摘要
    const senderNames = [...new Set(messageData.map(m => m.senderName))];
    const summary = senderNames.length > 3
        ? `${senderNames.slice(0, 3).join('、')}等人的聊天记录`
        : `${senderNames.join('、')}的聊天记录`;

    return {
        type: 'FORWARD',
        version: '1.0',
        title: options.title || '聊天记录',
        messageCount: messageData.length,
        messages: messageData,
        summary,
        createTime: Date.now()
    };
}

/**
 * 检查消息是否为转发消息
 * @param {Object} message - 消息对象
 * @returns {boolean}
 */
export function isForwardMessage(message) {
    if (!message || message.type !== 0) { // 必须是TEXT类型
        return false;
    }

    try {
        const content = typeof message.content === 'string'
            ? JSON.parse(message.content)
            : message.content;
        return content && content.type === 'FORWARD' && content.version;
    } catch (e) {
        return false;
    }
}

/**
 * 解析转发消息内容
 * @param {Object} message - 消息对象
 * @returns {ForwardMessageContent|null}
 */
export function parseForwardMessage(message) {
    if (!isForwardMessage(message)) {
        return null;
    }

    try {
        return typeof message.content === 'string'
            ? JSON.parse(message.content)
            : message.content;
    } catch (e) {
        console.error('[ForwardMessage] 解析转发消息失败:', e);
        return null;
    }
}

/**
 * 引用消息内容结构
 * @typedef {Object} QuotedMessageContent
 * @property {string} type - 固定值 "QUOTED"，用于标识带引用的消息
 * @property {string} version - 版本号，用于未来兼容性
 * @property {string} text - 实际发送的文本内容
 * @property {QuotedMessage[]} quotedMessages - 引用的消息列表
 */

/**
 * 引用的单条消息结构
 * @typedef {Object} QuotedMessage
 * @property {string|number} id - 原始消息ID
 * @property {number} senderId - 发送者用户ID
 * @property {string} sendNickName - 发送者昵称（优先显示）
 * @property {string} content - 消息文本内容
 * @property {number} type - 原始消息类型 (0:TEXT, 1:IMAGE, 2:FILE, 3:AUDIO)
 * @property {number} timestamp - 消息发送时间戳
 */

/**
 * 创建带引用的消息内容结构
 * @param {string} text - 要发送的文本内容
 * @param {Array} quotedMessages - 被引用的消息列表
 * @returns {QuotedMessageContent}
 */
export function createQuotedMessageContent(text, quotedMessages) {
    // 提取引用消息数据，优先使用 sendNickName
    const quotedData = quotedMessages.map(msg => ({
        id: msg.id || msg.tmpId,
        senderId: msg.sendId,
        sendNickName: msg.sendNickName || msg.senderName || msg.showName || '未知',
        senderAvatar: msg.senderAvatar || msg.headImage || '',
        content: msg.content || '',
        type: msg.type,
        timestamp: msg.sendTime
    }));

    return {
        type: 'QUOTED',
        version: '1.0',
        text,
        quotedMessages: quotedData
    };
}

/**
 * 检查消息是否为带引用的消息
 * @param {Object} message - 消息对象
 * @returns {boolean}
 */
export function isQuotedMessage(message) {
    if (!message || message.type !== 0) { // 必须是TEXT类型
        return false;
    }

    try {
        const content = typeof message.content === 'string'
            ? JSON.parse(message.content)
            : message.content;
        return content && content.type === 'QUOTED' && content.version;
    } catch (e) {
        return false;
    }
}

/**
 * 解析带引用的消息内容
 * @param {Object} message - 消息对象
 * @returns {QuotedMessageContent|null}
 */
export function parseQuotedMessage(message) {
    if (!isQuotedMessage(message)) {
        return null;
    }

    try {
        return typeof message.content === 'string'
            ? JSON.parse(message.content)
            : message.content;
    } catch (e) {
        console.error('[QuotedMessage] 解析引用消息失败:', e);
        return null;
    }
}

/**
 * 提取转发消息的文本内容（用于AI上下文）
 * @param {ForwardMessageContent} forwardData - 转发消息数据
 * @returns {string}
 */
export function extractTextForAI(forwardData) {
    const lines = [];

    lines.push(`=== 聊天记录 (${forwardData.messageCount}条消息) ===`);
    lines.push('');

    forwardData.messages.forEach(msg => {
        const time = new Date(msg.timestamp).toLocaleString('zh-CN');
        lines.push(`[${time}] ${msg.senderName}:`);

        switch (msg.type) {
            case 0: // TEXT
                lines.push(msg.content);
                break;
            case 1: // IMAGE
                lines.push('[图片消息]');
                break;
            case 2: // FILE
                lines.push(`[文件: ${msg.fileName}]`);
                break;
            case 3: // AUDIO
                lines.push('[语音消息]');
                break;
            default:
                lines.push('[其他类型消息]');
        }
        lines.push('');
    });

    lines.push('=== 聊天记录结束 ===');

    return lines.join('\n');
}

// ============================================
// 智能体任务状态卡片相关
// ============================================

/**
 * 智能体任务状态卡片内容结构
 * @typedef {Object} AgentTaskStatusContent
 * @property {string} type - 固定值 "AGENT_TASK_STATUS"
 * @property {string} version - 版本号 "1.0"
 * @property {string} taskId - 任务唯一标识
 * @property {string} [sessionId] - Happy会话ID（任务调度后返回）
 * @property {string} agentId - 智能体ID
 * @property {string} agentName - 智能体名称
 * @property {string} status - 当前状态: "initializing"|"running"|"waiting_confirmation"|"completed"|"error"
 * @property {TaskStatusItem[]} statusHistory - 状态历史记录
 * @property {ConfirmationPanel} [confirmationPanel] - 确认问题面板数据(当status为waiting_confirmation时)
 * @property {TaskResult} [result] - 任务结果(当status为completed或error时)
 * @property {number} createTime - 创建时间戳
 * @property {number} updateTime - 最后更新时间戳
 */

/**
 * 状态历史项
 * @typedef {Object} TaskStatusItem
 * @property {string} status - 状态: "initializing"|"running"|"waiting_confirmation"|"completed"|"error"
 * @property {string} message - 状态描述文本
 * @property {number} timestamp - 状态时间戳
 * @property {boolean} collapsed - 是否折叠显示(用于UI)
 */

/**
 * 确认问题面板
 * @typedef {Object} ConfirmationPanel
 * @property {string} question - 问题文本
 * @property {string} type - 问题类型: "text_input"|"options"|"both"
 * @property {Option[]} [options] - 选项列表(type为options或both时)
 * @property {string} [placeholder] - 输入框占位符(type为text_input或both时)
 * @property {boolean} required - 是否必答
 */

/**
 * 选项
 * @typedef {Object} Option
 * @property {string} value - 选项值
 * @property {string} label - 选项显示文本
 */

/**
 * 任务结果
 * @typedef {Object} TaskResult
 * @property {string} status - 结果状态: "success"|"error"
 * @property {string} message - 结果消息
 * @property {Object} [data] - 附加数据
 */

/**
 * 创建智能体任务状态内容
 * @param {string} taskId - 任务ID
 * @param {string} agentId - 智能体ID
 * @param {string} agentName - 智能体名称
 * @returns {AgentTaskStatusContent}
 */
export function createAgentTaskStatusContent(taskId, agentId, agentName) {
    return {
        type: 'AGENT_TASK_STATUS',
        version: '1.0',
        taskId,
        agentId,
        agentName,
        status: 'initializing',
        statusHistory: [
            {
                status: 'initializing',
                message: '正在初始化任务...',
                timestamp: Date.now(),
                collapsed: false
            }
        ],
        createTime: Date.now(),
        updateTime: Date.now()
    };
}

/**
 * 检查消息是否为智能体任务状态
 * @param {Object} message - 消息对象
 * @returns {boolean}
 */
export function isAgentTaskStatus(message) {
    if (!message || message.type !== 0) { // 必须是TEXT类型
        return false;
    }

    try {
        const content = typeof message.content === 'string'
            ? JSON.parse(message.content)
            : message.content;
        return content && content.type === 'AGENT_TASK_STATUS' && content.version;
    } catch (e) {
        return false;
    }
}

/**
 * 解析智能体任务状态
 * @param {Object} message - 消息对象
 * @returns {AgentTaskStatusContent|null}
 */
export function parseAgentTaskStatus(message) {
    if (!isAgentTaskStatus(message)) {
        return null;
    }

    try {
        return typeof message.content === 'string'
            ? JSON.parse(message.content)
            : message.content;
    } catch (e) {
        console.error('[AgentTaskStatus] 解析智能体任务状态失败:', e);
        return null;
    }
}

/**
 * 更新任务状态
 * @param {AgentTaskStatusContent} statusContent - 当前状态内容
 * @param {string} newStatus - 新状态
 * @param {string} message - 状态描述消息
 * @param {Object} extraData - 额外数据(如confirmationPanel、result等)
 * @returns {AgentTaskStatusContent}
 */
export function updateTaskStatus(statusContent, newStatus, message, extraData = {}) {
    const updatedContent = { ...statusContent };
    updatedContent.status = newStatus;
    updatedContent.updateTime = Date.now();

    // 折叠除最新状态外的所有历史状态
    updatedContent.statusHistory = updatedContent.statusHistory.map(item => ({
        ...item,
        collapsed: true
    }));

    // 添加新状态到历史
    updatedContent.statusHistory.push({
        status: newStatus,
        message,
        timestamp: Date.now(),
        collapsed: false
    });

    // 🔥 添加额外数据(如确认面板、结果等)
    // 只合并有值的字段，避免 undefined 覆盖已有的值（特别是 sessionId）
    Object.keys(extraData).forEach(key => {
        if (extraData[key] !== undefined && extraData[key] !== null) {
            updatedContent[key] = extraData[key];
        }
    });

    return updatedContent;
}

// ============================================
// @智能体提及消息相关
// ============================================

/**
 * @智能体提及消息内容结构
 * @typedef {Object} AgentMentionContent
 * @property {string} type - 固定值 "AGENT_MENTION"
 * @property {string} version - 版本号 "1.0"
 * @property {string} text - 任务描述内容
 * @property {AgentMentionInfo} agentMention - 智能体提及信息（包含初始状态）
 */

/**
 * 智能体提及信息（包含初始状态）
 * @typedef {Object} AgentMentionInfo
 * @property {string} agentId - 智能体ID
 * @property {string} agentName - 智能体名称
 * @property {string} taskId - 任务ID
 * @property {number} createTime - 创建时间戳
 * @property {string} status - 初始状态 "initializing"
 * @property {TaskStatusItem[]} statusHistory - 初始状态历史
 */

/**
 * 创建@智能体提及消息内容（包含初始状态）
 * @param {string} text - 任务描述
 * @param {string} agentId - 智能体ID
 * @param {string} agentName - 智能体名称
 * @param {string} taskId - 任务ID
 * @param {Array} quotedMessages - 引用的消息列表（可选）
 * @param {string} shareUrl - 任务详情页面URL（可选）
 * @returns {AgentMentionContent}
 */
export function createAgentMentionContent(text, agentId, agentName, taskId, quotedMessages, shareUrl) {
    const now = Date.now();
    const result = {
        type: 'AGENT_MENTION',
        version: '1.0',
        text,
        agentMention: {
            agentId,
            agentName,
            taskId,
            createTime: now,
            // 🔥 嵌入初始状态
            status: 'initializing',
            statusHistory: [
                {
                    status: 'initializing',
                    message: '正在初始化任务...',
                    timestamp: now,
                    collapsed: false
                }
            ]
        }
    };
    // 🔥 保存 shareUrl
    if (shareUrl) {
        result.agentMention.shareUrl = shareUrl;
    }
    if (quotedMessages && quotedMessages.length > 0) {
        result.quotedMessages = quotedMessages;
    }
    return result;
}

/**
 * 检查消息是否为@智能体提及消息
 * @param {Object} message - 消息对象
 * @returns {boolean}
 */
export function isAgentMentionMessage(message) {
    if (!message || message.type !== 0) { // 必须是TEXT类型
        return false;
    }

    try {
        const content = typeof message.content === 'string'
            ? JSON.parse(message.content)
            : message.content;
        return content && content.type === 'AGENT_MENTION' && content.version;
    } catch (e) {
        return false;
    }
}

/**
 * 解析@智能体提及消息内容
 * @param {Object} message - 消息对象
 * @returns {AgentMentionContent|null}
 */
export function parseAgentMentionMessage(message) {
    if (!isAgentMentionMessage(message)) {
        return null;
    }

    try {
        return typeof message.content === 'string'
            ? JSON.parse(message.content)
            : message.content;
    } catch (e) {
        console.error('[AgentMention] 解析@智能体提及消息失败:', e);
        return null;
    }
}
